/**
 * tourSteps — définition EXHAUSTIVE du tour guidé "Ultra-pro" Spotlight : chaque rubrique de
 * l'app est parcourue de haut en bas, fonctionnalité par fonctionnalité. Chaque étape cible un
 * vrai élément via son `data-testid` et déclenche, si besoin, une séquence de navigation
 * (`nav`) via le registre `tourNav` avant de chercher l'élément (changement d'onglet, sous-vue,
 * sous-onglet...). Si un élément est introuvable (ex: bannière conditionnelle non affichée pour
 * ce client), l'étape est automatiquement sautée par `TourSpotlight`.
 */
export const TOUR_STEPS = [
  // ───────────────────────────── Mon suivi > Aujourd'hui ─────────────────────────────
  {
    id: 'welcome',
    selector: 'tracking-dashboard-title',
    nav: [{ fn: 'setTab', arg: 'tracking' }, { fn: 'setTrackingView', arg: 'today' }],
    title: { fr: 'Bienvenue sur Body-Up !', en: 'Welcome to Body-Up!' },
    desc: { fr: "Découvrons ensemble TOUTES les fonctionnalités, rubrique par rubrique. Vous pourrez revoir ce tour à tout moment depuis Profil.", en: "Let's discover EVERY feature, section by section. You can replay this tour anytime from Profile." },
  },
  {
    id: 'guide-btn',
    selector: 'open-guide-btn',
    nav: [],
    title: { fr: 'Guide & conseils', en: 'Guide & tips' },
    desc: { fr: 'Retrouvez ici des explications détaillées sur le calcul de vos objectifs et macros.', en: 'Find detailed explanations here about how your goals and macros are calculated.' },
  },
  {
    id: 'quick-start',
    selector: 'quick-start-session-card',
    nav: [],
    title: { fr: 'Reprendre votre séance', en: 'Resume your session' },
    desc: { fr: "Quand une séance est en retard dans votre programme, elle apparaît ici pour la démarrer en un clic.", en: 'When a session is overdue in your program, it appears here to start it in one click.' },
  },
  {
    id: 'week-goals',
    selector: 'week-goals-widget',
    nav: [],
    title: { fr: 'Vos objectifs de la semaine', en: 'Your weekly goals' },
    desc: { fr: 'Suivez vos séances et vos jours de nutrition loggés, semaine par semaine.', en: 'Track your workouts and logged nutrition days, week by week.' },
  },
  {
    id: 'streak-badge',
    selector: 'streak-badge',
    nav: [],
    title: { fr: 'Votre série de jours actifs', en: 'Your active-day streak' },
    desc: { fr: 'Chaque jour où vous loggez une activité prolonge votre série. Ne la cassez pas !', en: 'Every day you log an activity extends your streak. Keep it alive!' },
  },
  {
    id: 'week-history',
    selector: 'week-goals-history-chart',
    nav: [],
    title: { fr: 'Historique des semaines', en: 'Weekly history' },
    desc: { fr: 'Comparez votre assiduité semaine après semaine en un coup d\u2019œil.', en: 'Compare your consistency week after week at a glance.' },
  },
  {
    id: 'add-meal',
    selector: 'add-meal-btn',
    nav: [],
    title: { fr: 'Journal alimentaire', en: 'Food journal' },
    desc: { fr: 'Ajoutez vos repas du jour ici — calories et macros se calculent automatiquement.', en: 'Add your meals here — calories and macros are calculated automatically.' },
  },
  {
    id: 'copy-yesterday',
    selector: 'copy-yesterday-btn',
    nav: [],
    title: { fr: "Copier hier", en: 'Copy yesterday' },
    desc: { fr: "Journée similaire à hier ? Copiez tout votre journal d'un clic.", en: 'Similar day to yesterday? Copy your entire journal in one click.' },
  },
  {
    id: 'goto-progress',
    selector: 'goto-progress-card',
    nav: [],
    title: { fr: 'Accès rapide à votre Progression', en: 'Quick access to your Progress' },
    desc: { fr: 'Ce raccourci vous amène directement à votre poids et vos mensurations.', en: 'This shortcut takes you straight to your weight and measurements.' },
  },

  // ───────────────────────────── Mon suivi > Progression / Profil ─────────────────────────────
  {
    id: 'avatar-3d',
    selector: 'avatar-3d-canvas',
    nav: [{ fn: 'setTrackingView', arg: 'progress' }],
    title: { fr: 'Avatar 3D interactif', en: 'Interactive 3D avatar' },
    desc: { fr: 'Suivez vos 20 mensurations précises en cliquant directement sur l\u2019avatar 3D, façon scan corporel professionnel.', en: 'Track your 20 precise measurements by clicking directly on the 3D avatar, like a professional body scan.' },
  },
  {
    id: 'nutrition-profile',
    selector: 'profile-view',
    nav: [{ fn: 'setTrackingView', arg: 'profile' }],
    title: { fr: 'Votre profil nutritionnel', en: 'Your nutrition profile' },
    desc: { fr: 'Renseignez votre profil pour des objectifs caloriques et macros calculés sur-mesure.', en: 'Fill in your profile for custom-calculated calorie and macro targets.' },
  },

  // ───────────────────────────── Nutrition ─────────────────────────────
  {
    id: 'nutrition-meal-plan',
    selector: 'tile-meal-plan',
    nav: [{ fn: 'setTab', arg: 'nutrition' }, { fn: 'setNutritionView', arg: 'hub' }],
    title: { fr: 'Plan alimentaire IA', en: 'AI meal plan' },
    desc: { fr: 'Générez un plan alimentaire complet et personnalisé par IA, avec recettes et liste de courses.', en: 'Generate a complete, personalized AI meal plan, with recipes and a shopping list.' },
  },
  {
    id: 'nutrition-calc-history',
    selector: 'tile-calc-history',
    nav: [],
    title: { fr: 'Historique des calculs', en: 'Calculation history' },
    desc: { fr: 'Retrouvez tous vos calculs de calories et macros passés.', en: 'Find all your past calorie and macro calculations.' },
  },
  {
    id: 'nutrition-recipes',
    selector: 'tile-recipes',
    nav: [],
    title: { fr: 'Recettes protéinées', en: 'High-protein recipes' },
    desc: { fr: '100+ recettes riches en protéines, à ajouter à votre journal en un clic.', en: '100+ high-protein recipes, added to your journal in one click.' },
  },
  {
    id: 'nutrition-personal-recipes',
    selector: 'tile-personal-recipes',
    nav: [],
    title: { fr: 'Vos recettes personnelles', en: 'Your personal recipes' },
    desc: { fr: 'Retrouvez les recettes générées automatiquement à partir de vos plans alimentaires.', en: 'Find recipes automatically generated from your meal plans.' },
  },
  {
    id: 'nutrition-recipe-of-day',
    selector: 'new-recipe-of-the-day-card',
    nav: [],
    title: { fr: 'Nouvelle recette du jour', en: "Today's new recipe" },
    desc: { fr: 'Une nouvelle recette riche en protéines mise en avant chaque jour.', en: 'A new high-protein recipe featured every day.' },
  },
  {
    id: 'nutrition-suggestion-of-day',
    selector: 'suggestion-of-the-day-card',
    nav: [],
    title: { fr: 'Suggestion du jour', en: "Today's suggestion" },
    desc: { fr: 'Une idée de repas adaptée à vos objectifs, suggérée automatiquement.', en: 'A meal idea adapted to your goals, suggested automatically.' },
  },

  // ───────────────────────────── Entraînements > Hub ─────────────────────────────
  {
    id: 'workouts-hub-programs',
    selector: 'tile-programs',
    nav: [{ fn: 'setTab', arg: 'workouts' }, { fn: 'setWorkoutsView', arg: 'hub' }],
    title: { fr: 'Vos programmes d\u2019entraînement', en: 'Your training programs' },
    desc: { fr: 'Musculation et Cardio : séances, tonnage, AMRAP — tout est suivi automatiquement.', en: 'Strength and Cardio: sessions, tonnage, AMRAP — everything tracked automatically.' },
  },
  {
    id: 'exercise-library',
    selector: 'tile-library',
    nav: [],
    title: { fr: 'Bibliothèque d\u2019exercices', en: 'Exercise library' },
    desc: { fr: '450+ exercices illustrés par IA, classés par groupe musculaire et matériel.', en: '450+ AI-illustrated exercises, sorted by muscle group and equipment.' },
  },
  {
    id: 'custom-library',
    selector: 'tile-custom-library',
    nav: [],
    title: { fr: 'Ma bibliothèque créée', en: 'My created library' },
    desc: { fr: 'Créez vos propres exercices et générez des visuels IA (image, storyboard, GIF) avec vos crédits mensuels.', en: 'Create your own exercises and generate AI visuals (image, storyboard, GIF) with your monthly credits.' },
  },
  {
    id: 'stats-tile',
    selector: 'tile-stats',
    nav: [],
    title: { fr: 'Statistiques & Records', en: 'Stats & Records' },
    desc: { fr: 'Vos badges RM, votre volume total d\u2019entraînement et votre palmarès de records personnels.', en: 'Your RM badges, total training volume and personal records hall of fame.' },
  },

  // ───────────────────────────── Entraînements > Programmes > Musculation ─────────────────────────────
  {
    id: 'workouts-new-program',
    selector: 'new-program-btn',
    nav: [{ fn: 'setWorkoutsView', arg: 'programs-hub' }, { fn: 'setProgramsSubTab', arg: 'musculation' }],
    title: { fr: 'Créer un programme Musculation', en: 'Create a strength program' },
    desc: { fr: '3 façons de créer un programme : manuellement, assisté par des questions, ou 100% généré par IA.', en: '3 ways to create a program: manually, question-assisted, or 100% AI-generated.' },
  },
  {
    id: 'methodology-guide',
    selector: 'methodology-guide-btn',
    nav: [],
    title: { fr: 'Guide méthodologique', en: 'Methodology guide' },
    desc: { fr: 'Toutes les explications sur les séries, charges et progressions expliquées simplement.', en: 'All explanations about sets, loads and progressions, simply explained.' },
  },
  {
    id: 'musculation-status-tabs',
    selector: 'musculation-status-tab-active',
    nav: [],
    title: { fr: 'Programmes actifs / archivés', en: 'Active / archived programs' },
    desc: { fr: 'Filtrez vos programmes actifs ou consultez vos anciens programmes archivés.', en: 'Filter your active programs or browse your old archived programs.' },
  },

  // ───────────────────────────── Entraînements > Programmes > Cardio ─────────────────────────────
  {
    id: 'workouts-cardio-new',
    selector: 'cardio-new-program-btn',
    nav: [{ fn: 'setProgramsSubTab', arg: 'cardio' }],
    title: { fr: 'Programmes Cardio', en: 'Cardio programs' },
    desc: { fr: 'HIIT, Tabata, EMOM, AMRAP, Intervalles... créez votre séance cardio en 1 clic.', en: 'HIIT, Tabata, EMOM, AMRAP, Intervals... build your cardio session in 1 click.' },
  },
  {
    id: 'cardio-guide',
    selector: 'cardio-guide-btn',
    nav: [],
    title: { fr: 'Guide Cardio', en: 'Cardio guide' },
    desc: { fr: 'Comprenez chaque méthode cardio et ses bénéfices avant de vous lancer.', en: 'Understand every cardio method and its benefits before you start.' },
  },
  {
    id: 'cardio-stats',
    selector: 'cardio-stats-btn',
    nav: [],
    title: { fr: 'Vos statistiques Cardio', en: 'Your cardio stats' },
    desc: { fr: 'Calories brûlées, temps total et progression de vos séances cardio.', en: 'Calories burned, total time and progression of your cardio sessions.' },
  },
  {
    id: 'cardio-leaderboard',
    selector: 'cardio-leaderboard-btn',
    nav: [],
    title: { fr: 'Classement Cardio', en: 'Cardio leaderboard' },
    desc: { fr: 'Comparez votre volume cardio à celui de la communauté Body-Up.', en: 'Compare your cardio volume to the Body-Up community.' },
  },
  {
    id: 'cardio-streak',
    selector: 'cardio-streak-badge',
    nav: [],
    title: { fr: 'Série cardio', en: 'Cardio streak' },
    desc: { fr: 'Votre série de séances cardio consécutives, pour rester motivé.', en: 'Your streak of consecutive cardio sessions, to stay motivated.' },
  },

  // ───────────────────────────── Entraînements > Statistiques ─────────────────────────────
  {
    id: 'stats-hub-stats',
    selector: 'stats-hub-tab-stats',
    nav: [{ fn: 'setWorkoutsView', arg: 'stats-hub' }, { fn: 'setStatsSubTab', arg: 'stats' }],
    title: { fr: 'Statistiques d\u2019entraînement', en: 'Training stats' },
    desc: { fr: 'Volume total, records de force et progression détaillée par groupe musculaire.', en: 'Total volume, strength records and detailed progression per muscle group.' },
  },
  {
    id: 'stats-hub-records',
    selector: 'stats-hub-tab-records',
    nav: [{ fn: 'setStatsSubTab', arg: 'records' }],
    title: { fr: 'Salle des Records', en: 'Hall of Records' },
    desc: { fr: 'Tous vos records personnels (1RM) réunis, badge par exercice.', en: 'All your personal records (1RM) in one place, badge per exercise.' },
  },

  // ───────────────────────────── Communauté ─────────────────────────────
  {
    id: 'community-coaching',
    selector: 'community-segment-coaching',
    nav: [{ fn: 'setTab', arg: 'community' }],
    title: { fr: 'Coaching', en: 'Coaching' },
    desc: { fr: 'Vos séances visio, check-ins et suivi personnalisé avec votre coach.', en: 'Your video sessions, check-ins and personalized coaching follow-up.' },
  },
  {
    id: 'community-social',
    selector: 'community-segment-community',
    nav: [],
    title: { fr: 'Communauté', en: 'Community' },
    desc: { fr: 'Partagez vos progrès et échangez avec les autres membres Body-Up.', en: 'Share your progress and chat with other Body-Up members.' },
  },
  {
    id: 'community-coach-chat',
    selector: 'community-segment-coach',
    nav: [],
    title: { fr: 'Messagerie Coach', en: 'Coach messaging' },
    desc: { fr: 'Un message direct et privé avec votre coach, à tout moment.', en: 'A direct, private message with your coach, anytime.' },
  },

  // ───────────────────────────── Profil ─────────────────────────────
  {
    id: 'profil-settings',
    selector: 'profil-settings-card',
    nav: [{ fn: 'setTab', arg: 'profil' }],
    title: { fr: 'Réglages', en: 'Settings' },
    desc: { fr: 'Langue, thème, installation de l\u2019app et activation des tours guidés.', en: 'Language, theme, app install and guided tours toggle.' },
  },
  {
    id: 'profil-replay',
    selector: 'profil-replay-tour-btn',
    nav: [],
    title: { fr: 'Revoir ce tour', en: 'Replay this tour' },
    desc: { fr: 'Vous pourrez toujours revenir ici pour relancer ce tour depuis le début.', en: 'You can always come back here to replay this tour from the start.' },
  },
  {
    id: 'subscription-plan',
    selector: 'current-plan-card',
    nav: [],
    title: { fr: 'Votre abonnement', en: 'Your subscription' },
    desc: { fr: 'Consultez votre plan actuel et changez de formule à tout moment.', en: 'View your current plan and switch formulas anytime.' },
  },
  {
    id: 'subscription-invoices',
    selector: 'my-invoices-section',
    nav: [],
    title: { fr: 'Factures & historique', en: 'Invoices & history' },
    desc: { fr: 'Toutes vos factures et séances de coaching passées, téléchargeables à tout moment.', en: 'All your past invoices and coaching sessions, downloadable anytime.' },
  },
];
